/**
 * "Did you catch any of these?" — the post-event catch-up list.
 *
 * THE CHORE THIS EXISTS TO KILL. A Community Day ends on Saturday with three
 * shinies in your bag. Recording them today means: open the Dex, switch to the
 * Shiny variant, search a name, scroll, tap — per Pokémon. So it does not get
 * done, and the dex that every other surface in this app reasons from goes
 * quietly stale. The app already knows what the event featured and already
 * knows what the trainer is missing; the only thing it has never done is ASK.
 *
 * So this takes the events that have just FINISHED and, for each, the species
 * the trainer is still missing from it — the exact same cross-reference the
 * Calendar runs for events that have not happened yet (eventDexGaps), pointed
 * backwards. The UI turns each one into a checkbox.
 *
 * WHAT IT DELIBERATELY LEAVES OUT. An event with nothing linked to it, and an
 * event where the trainer is already complete. Both would be a card that asks a
 * question with no answer worth giving, and this surface is only worth having
 * if every row on it is a row somebody would tap.
 *
 * Pure and storage-agnostic (the caller injects `getPresent`, `isAvailable` and
 * `now`) so the rules are testable without a DOM, a clock or the network.
 */
import { eventDexGaps } from "./eventDexGaps";
import { eventEndMs } from "./eventRecency";
import { isMaxBattleEventCategory } from "./raidDexCrossRef";
import type { RaidDexGap, RaidDexVariant, RaidSpawnInput } from "./raidDexCrossRef";
import type { CollectionFocus } from "./userPriorities";

/** One finished event, as much of it as the cross-reference needs. */
export interface CatchUpEventInput {
  id: string;
  title: string;
  /** Wall-clock event times, in any of the shapes eventCountdownMs accepts. */
  start?: string | null;
  end?: string | null;
  spawns?: readonly RaidSpawnInput[];
  /** Feed classification ("max-monday", "raid-hour", ...): a Max event's
   * spawns imply a Dynamax gap check. */
  category?: string | null;
  rawType?: string | null;
}

export interface CatchUpEntry {
  eventId: string;
  title: string;
  /** Whole days since it ended. 0 = it finished today. */
  endedDaysAgo: number;
  /** What the trainer is still missing from it, capped at `gapLimit`. */
  gaps: RaidDexGap[];
  /** The true count before the cap, so the UI can say "+4 more". */
  total: number;
}

/**
 * How many finished events the list may show at once.
 *
 * Three. A GO week can finish five things at once, and a screen of questions is
 * a screen nobody answers — the point of the prompt is that it looks like less
 * work than the chore it replaces.
 */
export const CATCH_UP_EVENT_LIMIT = 3;

/**
 * How many species one event may ask about. -1 is no cap, and is the default.
 *
 * It used to be eight, which left "+2 more you're missing from this one" under
 * a card whose entire job is to let a trainer tick off what they caught. A
 * capped form asks the trainer to go and finish the job somewhere else, which
 * is the errand this card exists to remove (user direction, 2026-08-14). The
 * option survives for a caller that genuinely wants a strip.
 */
export const CATCH_UP_GAP_LIMIT = -1;

/**
 * How far back an event may have ended and still be worth asking about.
 *
 * Thirty-six hours (user direction, 2026-08-17). A week was chosen when this
 * was a catch-all, but the memory it trades on is short: a trainer knows what
 * they caught last night and is guessing by Wednesday. Thirty-six hours covers
 * an evening event plus the whole of the next day, which is the window in which
 * the answer is still worth trusting.
 *
 * The prompt appears the moment the event ENDS — there is no delay by design,
 * and if one is observed it is the event's end time that is wrong, not this.
 */
export const CATCH_UP_LOOKBACK_HOURS = 36;

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

export interface CatchUpOptions {
  /** Device-local epoch ms. Injected so the rules are testable. */
  now: number;
  eventLimit?: number;
  gapLimit?: number;
  lookbackHours?: number;
}

/**
 * The finished events worth asking about, most recently finished first.
 *
 * `getPresent` and `isAvailable` are the same two predicates the Calendar strip
 * wires to dexStateStore and the availability roster, so a gap here can never
 * name a copy the trainer already has or a variant that species cannot carry.
 */
export function eventCatchUps(
  events: readonly CatchUpEventInput[],
  foci: readonly CollectionFocus[],
  getPresent: (variant: RaidDexVariant, speciesId: number, formId?: string) => boolean,
  isAvailable: (variant: RaidDexVariant, speciesId: number, formId?: string) => boolean,
  opts: CatchUpOptions,
): CatchUpEntry[] {
  const {
    now,
    eventLimit = CATCH_UP_EVENT_LIMIT,
    gapLimit = CATCH_UP_GAP_LIMIT,
    lookbackHours = CATCH_UP_LOOKBACK_HOURS,
  } = opts;
  const floor = now - lookbackHours * HOUR_MS;

  const dated: Array<{ event: CatchUpEventInput; endMs: number }> = [];
  for (const event of events) {
    const endMs = eventEndMs(event);
    // An event whose times cannot be read is dropped rather than kept: elsewhere
    // "unknown" means keep, but here an event with no end date cannot be said to
    // have FINISHED, and asking "did you catch these?" about something still
    // running is the one way this prompt can be actively wrong.
    if (!Number.isFinite(endMs)) continue;
    if (endMs >= now || endMs < floor) continue;
    dated.push({ event, endMs });
  }
  dated.sort((a, b) => b.endMs - a.endMs);

  const out: CatchUpEntry[] = [];
  for (const { event, endMs } of dated) {
    if (out.length >= eventLimit) break;
    const { gaps, total, hasLinkedSpecies } = eventDexGaps(
      event.spawns ?? [],
      foci,
      getPresent,
      isAvailable,
      gapLimit,
      // A finished Max Monday asks about the Dynamax catch too: "did you get
      // the Dynamax?" is the whole point of that event.
      { impliedDynamax: isMaxBattleEventCategory(event.category, event.rawType) },
    );
    if (!hasLinkedSpecies || gaps.length === 0) continue;
    out.push({
      eventId: event.id,
      title: event.title,
      // Whole days elapsed, floored: an event that ended six hours ago ended
      // "today", which is what a trainer would call it.
      endedDaysAgo: Math.max(0, Math.floor((now - endMs) / DAY_MS)),
      gaps,
      total,
    });
  }
  return out;
}

/*
 * `endedLabel` lived here and printed "Ended today" under each event's name.
 * It went on 2026-08-14: the card only ever shows events from the last week, so
 * the line spent a row telling a trainer something that was true of everything
 * in front of them. `endedDaysAgo` stays on the entry, because the ORDER still
 * depends on it and a future surface may want to say it.
 */
